import {
  ADVISORY_ENGAGEMENT_IDS,
  ADVISORY_QUESTION_IDS,
  ENGAGEMENT_DEFINITIONS,
  type AdvisoryEngagementId,
  type AdvisoryQuestionId,
} from '../../advisory/composites';
import type {
  AdvisoryBriefAnswerStatus,
  AdvisoryBriefDocumentInputV01,
  AdvisoryResearchContextV01,
} from '../../advisory/composites/ui';
import type {
  SupervisorAnswerRunState,
  SupervisorAnswerTaskProgress,
} from '../../hooks/advisory/advisoryAnswerRun';
import { reduceSupervisorAnswerEvent } from '../../hooks/advisory/advisoryAnswerRun';
import type { AdvisoryAnswerDocumentV01 } from '../../hooks/advisory/useAdvisoryAnswers';
import type {
  AdvisoryAnswersRunDoc,
  EngagementDoc,
  KeyQuestionDoc,
  StrategicAdvisoryDoc,
} from '../../redux/slices/advisory';

export interface AdvisoryLiveAnswersEnvironment {
  nodeEnv?: string;
  firebaseProjectId?: string;
}

export const isAdvisoryLiveAnswersEnabled = (
  environment: AdvisoryLiveAnswersEnvironment = {
    nodeEnv: process.env.NODE_ENV,
    firebaseProjectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  }
): boolean =>
  environment.nodeEnv === 'development' ||
  environment.nodeEnv === 'test' ||
  environment.firebaseProjectId === 'development-477809';

export type AdvisoryAnswerLifecycle =
  | { tag: 'none' }
  | { tag: 'active' }
  | { tag: 'completed' }
  | { tag: 'partial' }
  | { tag: 'failed' }
  | { tag: 'stale' };

const statusValue = (value: unknown): string => (typeof value === 'string' ? value : '');

export function deriveAdvisoryAnswerLifecycle(
  durable: AdvisoryAnswersRunDoc | null | undefined,
  orchestration: Record<string, unknown> | null,
  live: SupervisorAnswerRunState | null,
  stoppedWriting: boolean
): AdvisoryAnswerLifecycle {
  const durableStatus = durable?.status ?? '';
  const orchestrationStatus = statusValue(orchestration?.status);
  const active = ['answering', 'answers_running', 'running', 'active'].includes(
    durableStatus || orchestrationStatus
  );
  if (stoppedWriting && active) return { tag: 'stale' };
  if (active) return { tag: 'active' };
  const status = durableStatus || orchestrationStatus;
  if (['answers_completed', 'completed'].includes(status)) return { tag: 'completed' };
  if (['answers_partial', 'partial'].includes(status)) return { tag: 'partial' };
  if (['answers_failed', 'failed'].includes(status)) return { tag: 'failed' };
  if (live) return { tag: 'active' };
  return { tag: 'none' };
}

export interface AdvisoryAnswerProgressRow extends SupervisorAnswerTaskProgress {
  question: string;
}

function recoveredRun(
  orchestration: Record<string, unknown> | null,
  runId: string
): SupervisorAnswerRunState | null {
  if (!orchestration || !runId) return null;
  return reduceSupervisorAnswerEvent(
    null,
    'answersStarted',
    JSON.stringify({
      runId,
      engagementId: statusValue(orchestration.engagementId),
      rerunMode: statusValue(orchestration.rerunMode) || 'resume',
      recoveredAnswers: orchestration.recoveredAnswers,
      graph: orchestration.graph,
      trackingPath: '',
      answersPath: '',
    })
  );
}

export function buildAdvisoryAnswerProgressRows(
  live: SupervisorAnswerRunState | null,
  orchestration: Record<string, unknown> | null,
  runId: string,
  keyQuestions: readonly KeyQuestionDoc[],
  answerDocuments: readonly AdvisoryAnswerDocumentV01[]
): readonly AdvisoryAnswerProgressRow[] {
  const base = live ?? recoveredRun(orchestration, runId);
  const tasks = [...(base?.tasks ?? [])];
  keyQuestions.forEach(question => {
    if (tasks.some(task => task.label === question.id)) return;
    tasks.push({
      label: question.id,
      key: question.id,
      kind: 'key_question',
      questionId: question.id,
      order: typeof question.order === 'number' ? question.order : null,
      parent: '',
      status: '',
      winner: '',
      attempt: null,
      verdict: '',
      quality: null,
      answerStatus: '',
      facts: null,
      citations: null,
      wallSeconds: null,
      agent: '',
      phase: '',
      elapsedSeconds: null,
      deadlineSeconds: null,
    });
  });
  answerDocuments.forEach(answer => {
    const label = answer.label || answer.key;
    if (!label) return;
    const index = tasks.findIndex(task => task.label === label);
    if (index >= 0) {
      tasks[index] = {
        ...tasks[index],
        key: answer.key || tasks[index].key,
        kind: answer.kind || tasks[index].kind,
        questionId: answer.questionId || tasks[index].questionId,
        parent: answer.parent || tasks[index].parent,
        status: answer.status,
        answerStatus: answer.status,
        phase: '',
        agent: '',
        elapsedSeconds: null,
        deadlineSeconds: null,
      };
    } else {
      tasks.push({
        label,
        key: answer.key,
        kind: answer.kind,
        questionId: answer.questionId,
        order: answer.order,
        parent: answer.parent,
        status: answer.status,
        winner: '',
        attempt: null,
        verdict: '',
        quality: null,
        answerStatus: answer.status,
        facts: null,
        citations: null,
        wallSeconds: null,
        agent: '',
        phase: '',
        elapsedSeconds: null,
        deadlineSeconds: null,
      });
    }
  });
  const questionCopy = new Map(keyQuestions.map(question => [question.id, question.question]));
  return tasks.map(task => ({
    ...task,
    question:
      questionCopy.get(task.questionId) ??
      questionCopy.get(task.parent) ??
      task.questionId ??
      task.label,
  }));
}

const isQuestionId = (value: string): value is AdvisoryQuestionId =>
  (ADVISORY_QUESTION_IDS as readonly string[]).includes(value);
const isEngagementId = (value: string): value is AdvisoryEngagementId =>
  (ADVISORY_ENGAGEMENT_IDS as readonly string[]).includes(value);

const caveatFor = (status: AdvisoryBriefAnswerStatus): string | undefined => {
  switch (status) {
    case 'partial':
      return 'This answer is partial. Available sections are shown and gaps remain explicit.';
    case 'unanswerable':
      return 'The backend could not establish an answer. Any available explanation is retained below.';
    case 'failed':
      return 'This answer failed. Available content is retained, but it should not be treated as complete.';
    default:
      return undefined;
  }
};

export interface AdvisoryBriefBuildResult {
  model: AdvisoryBriefDocumentInputV01 | null;
  issues: readonly string[];
}

/** Workspace briefs describe the frozen goal of the enrolled run, not the intake history. */
export function bindWorkspaceBriefToRun(
  brief: AdvisoryBriefBuildResult,
  workspaceRunId: string | null,
  tracking: Record<string, unknown> | null
): AdvisoryBriefBuildResult {
  if (!brief.model) return brief;
  const goal =
    workspaceRunId &&
    tracking?.runId === workspaceRunId &&
    typeof tracking.goal === 'string' &&
    tracking.goal.trim()
      ? tracking.goal
      : '';
  return {
    ...brief,
    model: { ...brief.model, projectLabel: goal || 'Strategic advisory', subtitle: goal },
  };
}

export interface AdvisoryLiveResearchContextInput {
  context: AdvisoryResearchContextV01 | null;
  status: 'loading' | 'ready' | 'absent' | 'denied' | 'failed' | 'invalid' | 'disabled';
}

export function buildLiveAdvisoryBrief(
  project: StrategicAdvisoryDoc,
  engagement: EngagementDoc | null | undefined,
  keyQuestions: readonly KeyQuestionDoc[],
  documents: readonly AdvisoryAnswerDocumentV01[],
  research?: AdvisoryLiveResearchContextInput
): AdvisoryBriefBuildResult {
  const issues: string[] = [];
  const engagementIdValue = engagement?.engagementId ?? '';
  if (!isEngagementId(engagementIdValue)) {
    return { model: null, issues: ['The backend engagement identifier is not supported.'] };
  }
  const allowedQuestionIds = keyQuestions.flatMap(question =>
    isQuestionId(question.id) ? [question.id] : []
  );
  const questionById = new Map(keyQuestions.map(question => [question.id, question]));
  const topLevel = documents.filter(document => document.kind === 'key_question');
  const ordered = topLevel.map((document, inputIndex) => {
    const question = questionById.get(document.questionId);
    const readOrder = document.readOrder ?? question?.readOrder ?? null;
    if (readOrder === null) {
      issues.push(`Answer ${document.documentId} has no backend readOrder.`);
    }
    return { document, inputIndex, readOrder };
  });
  const counts = new Map<number, number>();
  ordered.forEach(item => {
    if (item.readOrder !== null) counts.set(item.readOrder, (counts.get(item.readOrder) ?? 0) + 1);
  });
  counts.forEach((count, readOrder) => {
    if (count > 1) issues.push(`Backend readOrder ${readOrder} is duplicated.`);
  });
  ordered.sort((left, right) => {
    if (left.readOrder === null && right.readOrder === null)
      return left.inputIndex - right.inputIndex;
    if (left.readOrder === null) return 1;
    if (right.readOrder === null) return -1;
    return left.readOrder - right.readOrder || left.inputIndex - right.inputIndex;
  });

  const answers = ordered.flatMap(({ document, readOrder }) => {
    if (!isQuestionId(document.questionId)) {
      issues.push(`Answer ${document.documentId} has an unknown questionId.`);
      return [];
    }
    if (!allowedQuestionIds.includes(document.questionId)) {
      issues.push(`Answer ${document.documentId} is not in the backend key-question set.`);
    }
    if (document.status === 'invalid') {
      issues.push(`Answer ${document.documentId} has an invalid terminal status.`);
      return [];
    }
    return [
      {
        questionId: document.questionId,
        answerKey: document.key || document.documentId,
        question: questionById.get(document.questionId)?.question || document.questionId,
        status: document.status,
        readOrder: readOrder ?? undefined,
        answerText: document.answerText,
        caveat: caveatFor(document.status),
        citations: document.citations,
        rendering: document.rendering,
      },
    ];
  });
  const engagementDefinition = ENGAGEMENT_DEFINITIONS[engagementIdValue];
  return {
    model: {
      engagementId: engagementIdValue,
      engagementLabel: engagement?.name || engagementDefinition.name,
      projectLabel: project.originalQuery || 'Strategic advisory',
      title: engagement?.name || engagementDefinition.name,
      subtitle: project.confirmedGoal || project.goal[0]?.goal || '',
      prepared: false,
      allowedQuestionIds,
      researchContext: research?.context,
      researchContextStatus:
        research && research.status !== 'disabled' ? research.status : undefined,
      answers,
    },
    issues,
  };
}
